import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import XCloseIcon from "../../shared/Icons/XCloseIcon";

const pixelFields = (pixel) => [
  { field: pixel.field, label: `${pixel.name} ID`, placeholder: pixel.placeholder },
  ...(pixel.eventFields || []),
];

const PixelTrackingModal = ({ pixel, venueOwnership, teamId, onClose, onSaved }) => {
  const fields = pixelFields(pixel);
  const [initialValues] = useState(() => Object.fromEntries(
    fields.map(({ field }) => [field, venueOwnership[field] || ''])
  ));
  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);

  const trimmedValues = Object.fromEntries(
    Object.entries(values).map(([field, fieldValue]) => [field, fieldValue.trim()])
  );
  const editingAllVenues = Boolean(venueOwnership.venueCount);
  const hasExistingValue = editingAllVenues
    ? Boolean(venueOwnership.hasExistingValue)
    : Boolean(venueOwnership[pixel.field]);
  const edited = fields.some(({ field }) => trimmedValues[field] !== initialValues[field].trim());
  const clearing = Object.values(trimmedValues).every((fieldValue) => fieldValue === '') && hasExistingValue && edited;
  const canSubmit = trimmedValues[pixel.field] !== '' || clearing;

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSave = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editingAllVenues) {
        const { data } = await client.patch(
          `/teams/${teamId}/venue_ownerships/bulk_update_tracking_pixel`,
          { tracking_pixel: { values: trimmedValues } }
        );
        toastSuccess(`${pixel.name} updated for ${venueOwnership.name}.`);
        onSaved(trimmedValues, data, venueOwnership.id);
        onClose();
        return;
      }

      await client.patch(`/teams/${teamId}/venue_ownerships/${venueOwnership.id}`, {
        venue_ownership: trimmedValues,
      });
      toastSuccess(`${pixel.name} updated for ${venueOwnership.name}.`);
      onSaved(trimmedValues, null, venueOwnership.id);
      onClose();
    } catch (error) {
      let message = 'Something went wrong. Please try again.';
      if (error.response && error.response.data) {
        const errors = error.response.data;
        if (Array.isArray(errors) && errors.length > 0) {
          message = errors.join(', ');
        } else if (typeof errors === 'string') {
          message = errors;
        }
      }
      toastError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="team-modal-overlay">
      <div
        className="team-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pixel-tracking-modal-title"
        style={{ maxWidth: 500, width: '90%', padding: 0, textAlign: 'left' }}
      >
        <div className="team-modal-header">
          <h3 className="team-modal-title" id="pixel-tracking-modal-title">{pixel.name}</h3>
          <button className="team-modal-close" onClick={onClose}>
            <XCloseIcon width="20" height="20" />
          </button>
        </div>
        <div className="team-modal-body">
          <p
            style={{
              margin: '0 0 16px',
              fontSize: 13,
              color: 'var(--sl-text-secondary, #414651)',
              lineHeight: 1.5,
            }}
          >
            {editingAllVenues ? (
              <>
                This will apply to <strong>all {venueOwnership.venueCount} venues</strong>, replacing
                any pixel a venue already has. One pixel shared across venues merges their audiences,
                and that cannot be undone in your ad account.
              </>
            ) : (
              <>
                This pixel applies only to <strong>{venueOwnership.name}</strong>. Shows at your
                other venues are unaffected.
              </>
            )}
          </p>
          {pixel.eventFieldsDescription && (
            <p
              style={{
                margin: '0 0 16px',
                fontSize: 13,
                color: 'var(--sl-text-secondary, #414651)',
                lineHeight: 1.5,
              }}
            >
              {pixel.eventFieldsDescription}
            </p>
          )}
          {fields.map(({ field, label, placeholder, hint }, index) => (
            <div className="team-form-group" key={field}>
              <label className="team-form-label" htmlFor={`pixel-tracking-${field}`}>{label}</label>
              <input
                id={`pixel-tracking-${field}`}
                className="team-form-input"
                type="text"
                value={values[field]}
                onChange={(e) => setValues({ ...values, [field]: e.target.value })}
                placeholder={placeholder}
                autoFocus={index === 0}
              />
              {hint && (
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: 12,
                    color: 'var(--sl-text-quaternary, #717680)',
                    lineHeight: 1.5,
                  }}
                >
                  {hint}
                </p>
              )}
            </div>
          ))}
        </div>
        <div className="team-modal-footer">
          <button className="team-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="team-btn-primary" disabled={submitting || !canSubmit} onClick={handleSave}>
            {submitting ? 'Saving...' : `${clearing ? 'Clear from' : 'Save for'} ${venueOwnership.name}`}
          </button>
        </div>
      </div>
    </div>
  );
};

PixelTrackingModal.propTypes = {
  pixel: PropTypes.shape({
    name: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    eventFieldsDescription: PropTypes.string,
    eventFields: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      hint: PropTypes.string,
    })),
  }).isRequired,
  venueOwnership: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string,
    venueCount: PropTypes.number,
    hasExistingValue: PropTypes.bool,
  }).isRequired,
  teamId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default PixelTrackingModal;
